// @ts-strict-ignore
import {
  ActionType,
} from 'flow/store/engine/constants';
import type {
  Action,
  IndexedRule,
  RuleObject,
  Rules,
  Threshold,
  ServicesByName,
  PopulatedThreshold,
} from 'flow/store/engine/types';

import type { ReasonCodeGroup } from '~/flow/types';

import type { RuleItemRefs, SemanticLine } from '../NodeEditorDrawer/types';
import { CaretPositionSuffixTypes } from '../NodeEditorDrawer/types';
import {
  getThresholdLeftDataType, isThresholdUsingNullOperator, populateThresholdData,
} from '../../../service/thresholds';


export function flattenRulesIndices({
  ruleToFlatten,
  prefixPath = '',
  thresholds,
  actions,
  reasonCodeGroups,
  services,
  pendingGroupOpeningParenthesisPath,
  supportsBestEffortParsing,
  morphingCaretPosition,
}: {
  ruleToFlatten: RuleObject;
  prefixPath?: string;
  thresholds: Record<string, Threshold>;
  actions: Record<string, Action>;
  reasonCodeGroups: ReasonCodeGroup[];
  services: ServicesByName;
  pendingGroupOpeningParenthesisPath?: string;
  supportsBestEffortParsing: boolean;
  morphingCaretPosition?: string;
}) {
  const flattenedRuleIndices = [];
  const ruleHasAndKey = ('and' in ruleToFlatten && ruleToFlatten.and);
  const ruleHasOrKey = ('or' in ruleToFlatten && ruleToFlatten.or);
  const flatSingleRule = 'id' in ruleToFlatten && thresholds[ruleToFlatten.id]; // checking thresholds[ruleToFlatten.id] because it'll throw an error when passed to populateThresholdData if it's undefined
  let prefixPathWithDash = '';
  if (prefixPath) {
    prefixPathWithDash = `${prefixPath}-`;
  } else if (!prefixPath && flatSingleRule) {
    prefixPathWithDash = '0-';
  }

  const pendingGroupOpenParenOpenedHereOrNotSet = currentPath => (
    !pendingGroupOpeningParenthesisPath
    || pendingGroupOpeningParenthesisPath === currentPath
  );

  if (flatSingleRule) {
    const threshold = thresholds[ruleToFlatten.id];
    const extendedThreshold = populateThresholdData({
      actions, reasonCodeGroups, services, threshold,
    });
    let pathTokenIndexNumber = 0;
    if (pendingGroupOpenParenOpenedHereOrNotSet(`${prefixPathWithDash}${pathTokenIndexNumber}`)) {
      flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!groupStartPending`);
    }
    flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!leftAction`);
    if (prefixPathWithDash.length > 0) {
      pathTokenIndexNumber += 1;
    }

    if (threshold.left?.attributeId) {
      flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!leftAttribute`);
      if (prefixPathWithDash.length > 0) pathTokenIndexNumber += 1;
    }

    const thresholdWithoutOperator = extendedThreshold.left.action.type === ActionType.TAG
      || extendedThreshold.left.action.type === ActionType.OUTCOME_REASON;

    if (!thresholdWithoutOperator) {
      flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!operator`);
      if (prefixPathWithDash.length > 0) pathTokenIndexNumber += 1;
    }

    if (threshold.right?.actionId) {
      flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!rightAction`);
      if (prefixPathWithDash.length > 0) pathTokenIndexNumber += 1;

      if (threshold.right?.attributeId) {
        flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!rightAttribute`);
        if (prefixPathWithDash.length > 0) pathTokenIndexNumber += 1;
      }
    } else {
      const leftDataType = getThresholdLeftDataType(extendedThreshold);
      const valueIsRange = extendedThreshold.operator === 'range' && String(extendedThreshold.right.attributeValue).includes(',');
      const rangeInput = ['integer', 'decimal', 'date', 'timestamp'].includes(leftDataType) && valueIsRange;
      const reasonCodeOperator = ['in', 'nin'].includes(extendedThreshold.operator) && leftDataType === 'reasoncodes';

      if (reasonCodeOperator) {
        flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!rightAttributeValueReasonCodeGroup`);
      } else if (rangeInput) {
        flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!rightAttributeValueRangeMin`);
        flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}!rightAttributeValueRangeMax`);
      } else {
        const type = thresholdWithoutOperator
          && extendedThreshold.left.action.type === ActionType.TAG
          ? '!rightTagActionResult'
          : '!rightAttributeValue';

        const isNullAttribute = isThresholdUsingNullOperator({
          leftDataType,
          threshold: extendedThreshold,
          supportsBestEffortParsing,
        });

        if (!isNullAttribute) {
          flattenedRuleIndices.push(`${prefixPathWithDash}${pathTokenIndexNumber}${type}`);
        }
      }
    }

    return flattenedRuleIndices;
  }

  if (ruleHasAndKey || ruleHasOrKey) {
    const key = ruleHasAndKey ? 'and' : 'or';
    const capitalizedKey = ruleHasAndKey ? 'And' : 'Or';
    const isAmbiguousRules = 'ambiguous' in ruleToFlatten || false;
    const flattenedRules = (ruleToFlatten[key] as Rules[]).reduce(
      (result: string[], rule: IndexedRule, index: number) => {
        const flattened = flattenRulesIndices({
          ruleToFlatten: rule,
          prefixPath: `${prefixPathWithDash}${key}-${index}`,
          thresholds,
          actions,
          reasonCodeGroups,
          services,
          pendingGroupOpeningParenthesisPath,
          supportsBestEffortParsing,
          morphingCaretPosition,
        });

        const resultWithRuleOperator = index > 0
          ? [
            ...result,
            `${prefixPathWithDash}${key}-${index}!ruleOperator${capitalizedKey}`,
          ]
          : result;

        return pendingGroupOpeningParenthesisPath === `${prefixPathWithDash}${key}-${index}`
          ? [
            ...resultWithRuleOperator,
            `${prefixPathWithDash}${key}-${index}!groupStart`,
            ...flattened,
          ]
          : [
            ...resultWithRuleOperator,
            ...flattened,
          ];
      }, []);

    return prefixPath && !isAmbiguousRules
      ? [
        `${prefixPath}!groupStart`,
        ...flattenedRules,
        `${prefixPath}!groupEnd`,
      ]
      : [
        ...flattenedRules,
      ];
  }

  return [];
}

export const generateSemanticLines = ({
  flatCaretIndices,
  ruleItemRefs,
  ruleEditorPanelBoxContainerWidth,
}: {
  flatCaretIndices: string[];
  ruleItemRefs: RuleItemRefs;
  ruleEditorPanelBoxContainerWidth: number;
}) => {
  const RULE_LINE_LEFT_SIDE_WIDTH_PADDING = 110;
  // this hash is for rendering key
  const groupedByLines: SemanticLine[] = [{
    items: [],
    id: 0,
    width: 0,
  }];
  flatCaretIndices.forEach((caret, index) => {
    const matchingRuleItemRef = ruleItemRefs[caret];
    const itemRefWidth = matchingRuleItemRef
      ? matchingRuleItemRef.width
      : 0;

    // wait until width is other than 0 (for outcome island)
    const lineWidthBeyondContainerWidth = ruleEditorPanelBoxContainerWidth
      ? groupedByLines.at(-1).width + itemRefWidth + RULE_LINE_LEFT_SIDE_WIDTH_PADDING
      >= ruleEditorPanelBoxContainerWidth
      : false;
    if (
      caret.includes(CaretPositionSuffixTypes.RULE_OPERATOR)
      || caret.endsWith(CaretPositionSuffixTypes.GROUP_START)
      || caret.endsWith(CaretPositionSuffixTypes.GROUP_END)
      || (
        lineWidthBeyondContainerWidth
        && groupedByLines.length > 1
      )
    ) {
      // if this is first item in the entire indices and also either rule operator and/or OR group start end,
      // we break line immediately after this item.
      // if not the first item, we also check if this is group start parenthesis item. if so, we always break line
      // the key thing is it breaks line for the item after this one. this one goes into the last line created so far.
      const nextCaret = flatCaretIndices[index + 1];
      const isNextCaretAnotherRuleOperator = nextCaret
        ?.includes(CaretPositionSuffixTypes.RULE_OPERATOR);
      const isNextCaretGroupEnd = nextCaret
        ?.includes(CaretPositionSuffixTypes.GROUP_END);
      if (
        (caret.includes(CaretPositionSuffixTypes.RULE_OPERATOR) && index === 0)
        || caret.endsWith(CaretPositionSuffixTypes.GROUP_START)
        || (
          !lineWidthBeyondContainerWidth && !isNextCaretAnotherRuleOperator
          && !caret.includes(CaretPositionSuffixTypes.RULE_OPERATOR)
          && !caret.includes(CaretPositionSuffixTypes.GROUP_END)
        )
      ) {
        groupedByLines.at(-1).items.push(caret);
        groupedByLines.at(-1).width += itemRefWidth;

        // if the next caret has "!ruleOperator" string suffix in it, it means in the next turn of this iteration, it'll create another new line
        // with that next caret in it. (see the else block down right below)
        // in that case, this following new empty line will render useless, and we want to avoid that.
        if (index !== flatCaretIndices.length - 1
          && !isNextCaretAnotherRuleOperator && !isNextCaretGroupEnd) {
          groupedByLines.push({
            items: [],
            id: groupedByLines.length + 1,
            width: 0,
          });
        }
      } else {
        // otherwise we just create a new line with this item. meaning the item will go into a new line.
        groupedByLines.push({
          items: [caret],
          id: groupedByLines.length + 1,
          width: itemRefWidth,
        });
      }
    } else {
      // just add to the current last line created so far.
      groupedByLines.at(-1).items.push(caret);
      groupedByLines.at(-1).width += itemRefWidth;
    }
  });

  return {
    groupedByLines,
    lineBreakPointIndices: groupedByLines.slice(1).map(group => (group.items[0])),
  };
};

export const getDollarCurrencyRightAttributeValueMode = (thresholdLeft: PopulatedThreshold['left']) => {
  if (!thresholdLeft?.service || !thresholdLeft?.attribute) return false;
  const leftAttributeTHMLA = [
    'th_multilevel',
    'th_nonmonetary_multilevel',
  ].includes(thresholdLeft.attribute.attribute_name);
  const mlaFormulatAttributeComponent = thresholdLeft.attributeComponents?.find(
    component => component.attributeComponent.placeholder === '{formula}');

  if (!mlaFormulatAttributeComponent?.value) return false;
  const mlaFormulaConfiguredForDollarRHSMode = [
    'sum',
    'avg',
    'min',
    'max',
    'st_dev_amount',
  ].includes(mlaFormulatAttributeComponent.value as string);

  return (
    leftAttributeTHMLA
      && mlaFormulatAttributeComponent
      && mlaFormulaConfiguredForDollarRHSMode
  );
};
